import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import QuickDurationPredictionForm, { InputData } from "./QuickDurationPredictionForm";
import { calculateDurationMin, calculateBreakTimeCorrection, formatDuration } from "./modelDetails.lib";
import { niceNumber, classNames } from "@/lib/util";
import type { ModelData } from "./modelDetails.lib";
import warningIcon from "@/img/warning-new.svg";

interface Props {
    model: ModelData;
    breakTimeMin: number;
    setBreakTimeMin: (value: number) => void;
}

const SHIFT_DURATION_MIN = 480;

/**
 * Displays a form for quick duration prediction and the results.
 * Allows changing the quantity, price per hour, break time, and setup time.
 * Shows the model prediction, normative baseline, break-time correction details, and cost analysis.
 */
export default function QuickDurationPrediction({ model, breakTimeMin, setBreakTimeMin }: Props) {
    const { t } = useLingui();
    const [quantity, setQuantity] = useState(100);
    const [pricePerHour, setPricePerHour] = useState(0);
    const [includeSetup, setIncludeSetup] = useState(true);
    const [useHours, setUseHours] = useState(true);

    // Numbers have string types to make forms happy
    const initialInputs: InputData = {
        quantity: String(quantity),
        pricePerHour: String(pricePerHour),
        breakTimeMin: String(breakTimeMin),
        includeSetup: true,
        useHours: true,
    };

    const updateInputs = (data: InputData) => {
        // Normalize to zero if NaN
        setQuantity(Number(data.quantity) || 0);
        setPricePerHour(Number(data.pricePerHour) || 0);
        setBreakTimeMin(Number(data.breakTimeMin) || 0);
        setIncludeSetup(data.includeSetup);
        setUseHours(data.useHours);
    };

    const modelDurationMin = calculateDurationMin(
        quantity,
        includeSetup ? model.modelSetup : 0,
        model.modelSpeed,
        breakTimeMin
    );

    const normativeDurationMin = calculateDurationMin(
        quantity,
        includeSetup ? model.normativeSetup : 0,
        model.normativeSpeed,
        breakTimeMin
    );

    const durationDiff = modelDurationMin - normativeDurationMin;
    const correction = calculateBreakTimeCorrection(breakTimeMin);

    const breakTimeCorrectionDetails =
        breakTimeMin > 0
            ? [
                  {
                      label: t`Break time/shift` + ":",
                      value: formatDuration(breakTimeMin, useHours),
                  },
                  {
                      label: t`Break time ratio` + ":",
                      value: `${niceNumber(correction.breakTimeRatio * 100, 2)}%`,
                  },
                  {
                      label: t`Shift duration` + ":",
                      value: formatDuration(SHIFT_DURATION_MIN, useHours),
                  },
                  {
                      label: t`Speed reduction` + ":",
                      value: `-${niceNumber(correction.speedReduction, 2)}%`,
                      special: "danger",
                  },
                  {
                      label: t`Effective Work Time` + ":",
                      value: formatDuration(correction.effectiveWorkTime, useHours),
                  },
                  {
                      label: t`Effective Speed Factor` + ":",
                      value: niceNumber(1 - correction.breakTimeRatio, 3),
                      special: "success",
                  },
              ]
            : null;

    // Price calculations
    const modelPrice = (modelDurationMin / 60) * pricePerHour; // Convert minutes to hours
    const normativePrice = (normativeDurationMin / 60) * pricePerHour;
    const priceDiff = modelPrice - normativePrice;

    // Check for model fallback conditions
    const productionTimeMin = (quantity / model.modelSpeed) * 60;
    const hasModelFallback = productionTimeMin < model.modelFallbackThresholdH * 60;

    return (
        <div className="card p-0">
            <div className="card-title card-title-border-bottom p-3">{t`Quick Duration Prediction`}</div>

            <div className="m-3">
                <QuickDurationPredictionForm initialInputs={initialInputs} onUpdate={updateInputs} />
            </div>

            {quantity > 0 && (
                <div className="nested-cards d-flex flex-column gap-3 m-3">
                    <div className="card">
                        <div className="d-flex justify-content-between gap-3 height-100">
                            <div>
                                <div className="card-title card-title-small">{t`Model Prediction`}</div>
                                {includeSetup && (
                                    <div className="card-label">
                                        {t`Setup`}: {niceNumber(model.modelSetup, 2)} {t`min`}
                                    </div>
                                )}
                            </div>
                            <div className="d-flex flex-column justify-content-between align-items-end gap-3">
                                <span
                                    className={classNames("card-badge", {
                                        "variant-success": durationDiff < 0,
                                        "variant-danger": durationDiff > 0,
                                        "variant-neutral": durationDiff === 0,
                                    })}
                                >
                                    {durationDiff < 0 ? "-" : durationDiff > 0 ? "+" : ""}
                                    {formatDuration(Math.abs(durationDiff), useHours)}{" "}
                                    {durationDiff < 0 ? t`faster` : durationDiff > 0 ? t`slower` : t`equal`}
                                </span>
                                <div className="card-value mb-2">{formatDuration(modelDurationMin, useHours)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="card card-neutral">
                        <div className="d-flex justify-content-between gap-3 height-100">
                            <div>
                                <div className="card-title card-title-small">{t`Normative Baseline`}</div>
                                {includeSetup && (
                                    <div className="card-label">
                                        {t`Setup`}: {niceNumber(model.normativeSetup, 2)} {t`min`}
                                    </div>
                                )}
                            </div>
                            <div className="d-flex flex-column justify-content-between align-items-end gap-3">
                                <div className="card-value mb-2">{formatDuration(normativeDurationMin, useHours)}</div>
                            </div>
                        </div>
                    </div>

                    {breakTimeCorrectionDetails && (
                        <div className="card card-success p-0">
                            <div className="card-title card-title-small m-3">{t`Break-time Correction Details`}</div>

                            <div className="auto-grid auto-grid--border-bottom">
                                {breakTimeCorrectionDetails.map(({ label, value, special }) => (
                                    <div className="card-row card-row-border-bottom" key={label}>
                                        <span className="card-label">{label}</span>
                                        <span
                                            className={`card-value card-value--small${special ? ` text-${special}` : ""}`}
                                        >
                                            {value}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="note p-3">
                                <strong>{t`Note`}:</strong>{" "}
                                {t`The production speed is reduced by ${correction.speedReduction.toFixed(1)}% to account for break-time, making duration predictions more realistic for actual working conditions.`}
                            </div>
                        </div>
                    )}

                    {hasModelFallback && (
                        <div className="card card-warning">
                            <div className="d-flex justify-content-between align-items-start gap-3 height-100">
                                <img src={warningIcon} alt={t`Warning`} />

                                <div className="flex-grow-1">
                                    <div className="card-title card-title-small">{t`Model Fallback to Normative`}</div>
                                    <div className="card-label pr-2">
                                        {t`Production time is less than limit. Model estimation falls back to normative baseline for reliability.`}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {pricePerHour > 0 && (
                        <div
                            className={classNames(`card gap-3`, {
                                "card-success": priceDiff < 0,
                                "card-danger": priceDiff > 0,
                                "card-neutral": priceDiff === 0,
                            })}
                        >
                            <div className="card-title">{t`Cost Analysis`}</div>
                            <div className="card-row align-items-start">
                                <span className="card-label">{t`Model Cost`}:</span>
                                <span className="d-flex flex-column align-items-end">
                                    <span className="card-value">{niceNumber(modelPrice, 2)}</span>
                                    <span className="card-value card-value--tiny">
                                        {priceDiff < 0 ? "-" : priceDiff > 0 ? "+" : ""}
                                        {Math.abs(priceDiff).toFixed(2)}{" "}
                                        {priceDiff < 0 ? t`cheaper` : priceDiff > 0 ? t`more expensive` : t`equal`}
                                    </span>
                                </span>
                            </div>
                            <div className="card-row align-items-start">
                                <span className="card-label">{t`Normative Cost`}:</span>
                                <span className="d-flex flex-column align-items-end">
                                    <span className="card-value">{niceNumber(normativePrice, 2)}</span>
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
